<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Settlements</h1>
    <DownloadButton
      title="Download CSV"
      label="Download"
      :downloading="isDownloadingCsv"
      :disabled="settlements.length === 0"
      @click="downloadCsv"
    />
  </div>
  <ErrorDisplay v-if="downloadErrorMessage" :error-message="downloadErrorMessage" />
  <WarningDisplay v-if="showSlowLoadWarning" />
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <SettlementsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />

    <DataTable
      :is-loading="isLoading"
      :columns="settlementsTableColumns"
      :data="settlements"
      :total-count="totalCount"
      :page-number="pageNumber"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import DownloadButton from '@/components/common/DownloadButton.vue';
import ErrorDisplay from '@/components/common/ErrorDisplay.vue';
import WarningDisplay from '@/components/common/WarningDisplay.vue';
import { fetchSettlements, type SettlementsServiceGetReportRequest } from '@/services/settlements';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { handleError } from '@/utils/error.util';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { formatDateShort, toRandString } from '@/utils/format.utils';
import { addMetric } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref, watch } from 'vue';
import type { ModelSettlementReport } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import SettlementsControls from '../settlements-controls/SettlementsControls.vue';
import { settlementsTableColumns } from './SettlementsTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(true);
const isDownloadingCsv = ref(false);
const downloadErrorMessage = ref('');
const settlementsParams = ref<SettlementsServiceGetReportRequest>({ sort: '-batch' });
const previousParams = ref<SettlementsServiceGetReportRequest>({ ...settlementsParams.value });
const settlements = ref<ModelSettlementReport[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);
const showLoadWarning = ref(false);
const SLOW_LOAD_WARNING_DELAY_MS = 3000;
let slowLoadWarningTimeout: ReturnType<typeof setTimeout> | null = null;
const showSlowLoadWarning = computed(() => showLoadWarning.value);

watch(
  () => isLoading.value || isDownloadingCsv.value,
  (inProgress, _prev, onCleanup) => {
    onCleanup(() => {
      if (slowLoadWarningTimeout) {
        clearTimeout(slowLoadWarningTimeout);
        slowLoadWarningTimeout = null;
      }
    });

    if (inProgress) {
      showLoadWarning.value = false;
      if (slowLoadWarningTimeout) {
        clearTimeout(slowLoadWarningTimeout);
      }
      slowLoadWarningTimeout = setTimeout(() => {
        if (isLoading.value || isDownloadingCsv.value) {
          showLoadWarning.value = true;
        }
      }, SLOW_LOAD_WARNING_DELAY_MS);
      return;
    }

    if (slowLoadWarningTimeout) {
      clearTimeout(slowLoadWarningTimeout);
      slowLoadWarningTimeout = null;
    }
    showLoadWarning.value = false;
  },
  { immediate: true },
);

onMounted(async () => {
  await onUpdateFilter(settlementsParams.value, true);
  await useUserStore().getSessionExpiry();
});

const onUpdateFilter = async (params: SettlementsServiceGetReportRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  settlements.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchSettlements(params);
    settlements.value = response.settlements ?? [];
    totalCount.value = response.totalCount || 0;

    addMetric('settlements-table', { status: 'success', channel: 'merchant', method: 'fetchSettlements' });
  } catch (error) {
    handleError(error, { metricName: 'settlements-table' });
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = (value: DateRange): void => {
  settlementsParams.value.fromDate = dayjs(`${value.start?.year}-${value.start?.month}-${value.start?.day}`)
    .startOf('day')
    .toISOString();
  settlementsParams.value.toDate = dayjs(`${value.end?.year}-${value.end?.month}-${value.end?.day}`)
    .endOf('day')
    .toISOString();

  onUpdateFilter(settlementsParams.value);
};

const onClearFilters = (): void => {
  pageNumber.value = 1;
  settlementsParams.value = { skip: 0, sort: '-batch' };
  onUpdateFilter(settlementsParams.value);
};

const onSearchTermChange = (value: string): void => {
  settlementsParams.value.search = value.toUpperCase();
  onUpdateFilter(settlementsParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  settlementsParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(settlementsParams.value);
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  generalStore.pageSize = size;
  settlementsParams.value.limit = size;
  onUpdateFilter(settlementsParams.value);
};

const downloadCsv = async () => {
  isDownloadingCsv.value = true;
  downloadErrorMessage.value = '';
  try {
    const settlements = await fetchAllPaginated<ModelSettlementReport, SettlementsServiceGetReportRequest>(
      fetchSettlements,
      settlementsParams.value,
      'settlements',
    );
    if (settlements.length === 0) {
      console.warn('No data available for export');
      return;
    }

    const headerMap: { [Property in keyof ModelSettlementReport]: string } = {
      batch: 'Settlement Batch',
      reference: 'Settlement Reference',
      settledAt: 'Settled Date',
      amount: 'Settlement Amount',
      batchType: 'Batch Type',
    };
    const data = settlements.map((settlement) => {
      return {
        ...settlement,
        amount: toRandString(settlement.amount ?? 0),
        settledAt: settlement.settledAt ? formatDateShort(settlement.settledAt) : '',
      };
    });

    const url = generateCsvBlobUrl(headerMap, data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `settlements-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    downloadErrorMessage.value = handleError(error, {
      metricName: 'settlements-table-csv-download',
      networkMessage:
        'Network error occurred while downloading settlements CSV. Please check your connection and try again.',
      unexpectedMessage: 'An unexpected error occurred while downloading settlements CSV. Please try again later.',
    });
  } finally {
    isDownloadingCsv.value = false;
  }
};
</script>
